/**
 * Mirrors SagaType and FRIENDLY_SAGA_NAMES in backend/app/utils/util.py.
 *
 * SagaType isn't referenced by any exposed schema, so it doesn't come through
 * `yarn genapi` and this copy is maintained by hand. Saga types are added
 * rarely; when one is, add it here too. `FRIENDLY_SAGA_NAMES` is typed as a
 * total Record, so adding to SAGA_TYPES without a label is a compile error.
 */
const SAGA_TYPES = [
  'create_order',
  'update_order',
  'progress_bol',
  'remove_bol',
  'progress_bol_image',
  'progress_drop',
  'progress_status',
  'cancel_order',
  'reset_order',
  'add_invoice',
  'accept_order_request',
  'reject_order_request',
  'upsert_supply_plans',
] as const

export type SagaType = (typeof SAGA_TYPES)[number]

export const FRIENDLY_SAGA_NAMES: Record<SagaType, string> = {
  create_order: 'Order Creation',
  update_order: 'Order Update',
  progress_bol: 'Add BOL',
  remove_bol: 'Remove BOL',
  progress_bol_image: 'Add BOL Image',
  progress_drop: 'Save Drop',
  progress_status: 'Status Update',
  cancel_order: 'Cancel Order',
  reset_order: 'Reset Order',
  add_invoice: 'Add Invoice',
  accept_order_request: 'Accept Order',
  reject_order_request: 'Reject Order',
  upsert_supply_plans: 'Sync Supply Plans',
}

export function isSagaType(value: string): value is SagaType {
  return value in FRIENDLY_SAGA_NAMES
}

/**
 * Display label for a log's `saga_type`, which is a bare string on the API.
 * Unrecognised values fall through unchanged rather than being hidden.
 */
export function friendlySagaName(
  sagaType: string | null | undefined,
): string | null {
  if (!sagaType) return null
  return isSagaType(sagaType) ? FRIENDLY_SAGA_NAMES[sagaType] : sagaType
}

/**
 * The sagas that translate an order or invoice and so leave a translation
 * audit behind, keyed by (log group, saga id). Every other saga has no audit
 * to show, and an audit button on those rows only leads to an empty modal.
 *
 * Supply plan sagas are absent on purpose: their audit is embedded on the plan
 * and opened from the Supply Plans grid, not looked up by saga id.
 */
const SAGAS_WITH_TRANSLATION_AUDIT: readonly SagaType[] = [
  'create_order',
  'update_order',
  'progress_bol',
  'progress_drop',
  'add_invoice',
]

export function hasTranslationAudit(
  sagaType: string | null | undefined,
): boolean {
  return (
    !!sagaType &&
    isSagaType(sagaType) &&
    SAGAS_WITH_TRANSLATION_AUDIT.includes(sagaType)
  )
}
